export type CaptionPanelPlaybackWord = {
  readonly index: number;
  readonly absStart: number;
  readonly absEnd: number;
};

export type CaptionPanelPlaybackContext = {
  readonly displayWords: readonly CaptionPanelPlaybackWord[];
};

export type CaptionPanelPlayback = {
  /** Comma-delimited indexes remain equal while a row's active words do not change. */
  readonly activeWordIndexesByItem: ReadonlyMap<string, string>;
  /** The first visible active word alone owns auto-scroll. */
  readonly firstActiveWordId: string | null;
};

/** One playback scan serves every visible lane's highlights and the first-word scroll. */
export function projectCaptionPanelPlayback<T extends CaptionPanelPlaybackContext>(args: {
  readonly visibleItemIds: readonly string[];
  readonly captionContexts: ReadonlyMap<string, T>;
  readonly currentFrame: number;
  readonly fps: number;
  readonly convertMsToFrame: (ms: number, boundary: 'start' | 'end', fps: number) => number;
}): CaptionPanelPlayback {
  const activeWordIndexesByItem = new Map<string, string>();
  let firstActiveWordId: string | null = null;

  for (const itemId of args.visibleItemIds) {
    const context = args.captionContexts.get(itemId);
    if (!context) continue;

    let indexes = '';
    for (const word of context.displayWords) {
      const start = args.convertMsToFrame(word.absStart, 'start', args.fps);
      const end = Math.max(start + 1, args.convertMsToFrame(word.absEnd, 'end', args.fps));
      if (start > args.currentFrame || args.currentFrame >= end) continue;
      indexes += `,${word.index},`;
      firstActiveWordId ??= `${itemId}-${word.index}`;
    }
    if (indexes) activeWordIndexesByItem.set(itemId, indexes);
  }

  return {activeWordIndexesByItem, firstActiveWordId};
}
